import React, { useState, useEffect } from 'react';
import { MapPin, Check, X, AlertCircle } from 'lucide-react';
import { weatherService } from '../../lib/weather-service';

/**
 * Weather Location Configuration Component
 * Allows users to set their preferred weather location for the Intelligence Dashboard
 */
export default function WeatherLocationConfig({ onClose, onLocationUpdated }) {
  const [location, setLocation] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isTestingLocation, setIsTestingLocation] = useState(false);

  useEffect(() => {
    // Get current location setting
    const current = weatherService.getDefaultLocation();
    setCurrentLocation(current);
    setLocation(current);
  }, []);

  const handleSaveLocation = async () => {
    if (!location.trim()) {
      setError('Please enter a location');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Test the location by fetching weather data
      setIsTestingLocation(true);
      const testWeather = await weatherService.getCurrentWeather(location.trim());
      
      if (testWeather && testWeather.location) {
        // Location is valid, save it
        weatherService.setLocation(location.trim());
        onLocationUpdated?.(location.trim(), testWeather.location);
        onClose?.();
      } else {
        setError('Could not find weather data for this location');
      }
    } catch (err) {
      console.error('Error testing location:', err);
      setError('Could not validate location. Please check the format (e.g., "City,State,Country")');
    } finally {
      setIsLoading(false);
      setIsTestingLocation(false);
    }
  };

  const handleCancel = () => {
    setLocation(currentLocation);
    setError('');
    onClose?.();
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    setError('');
  };

  const formatLocationHint = () => {
    return 'Format: "City,State,Country" (e.g., "Raleigh,NC,US" or "London,UK")';
  };

  return (
    <div className="weather-location-config">
      <div className="config-overlay" onClick={handleCancel}></div>
      <div className="config-modal">
        <div className="config-header">
          <div className="config-title">
            <MapPin className="config-icon" size={20} />
            <span>Weather Location</span>
          </div>
          <button className="config-close" onClick={handleCancel}>
            <X size={18} />
          </button>
        </div>

        <div className="config-content">
          <div className="config-section">
            <label className="config-label">
              Current Location: <span className="current-location">{currentLocation}</span>
            </label>
          </div>

          <div className="config-section">
            <label className="config-label" htmlFor="location-input">
              New Location
            </label>
            <input
              id="location-input"
              type="text"
              className="config-input"
              value={location}
              onChange={handleLocationChange}
              placeholder="e.g., Raleigh,NC,US"
              disabled={isLoading}
            />
            <div className="config-hint">
              {formatLocationHint()}
            </div>
          </div>

          {error && (
            <div className="config-error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {isTestingLocation && (
            <div className="config-testing">
              <div className="testing-spinner"></div>
              <span>Testing location...</span>
            </div>
          )}
        </div>

        <div className="config-actions">
          <button 
            className="config-btn config-btn-secondary" 
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            className="config-btn config-btn-primary" 
            onClick={handleSaveLocation}
            disabled={isLoading || !location.trim()}
          >
            {isLoading ? (
              <>
                <div className="btn-spinner"></div>
                Testing...
              </>
            ) : (
              <>
                <Check size={16} />
                Save Location
              </>
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .weather-location-config {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .config-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
        }

        .config-modal {
          position: relative;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
          width: 100%;
          max-width: 480px;
          margin: 0 20px;
          animation: configSlideIn 0.2s ease-out;
        }

        @keyframes configSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .config-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 24px 0 24px;
          margin-bottom: 20px;
        }

        .config-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 18px;
          font-weight: 600;
          color: var(--color-slate-900);
        }

        .config-icon {
          color: var(--color-teal-500);
        }

        .config-close {
          background: none;
          border: none;
          padding: 4px;
          border-radius: 6px;
          color: var(--color-stone-500);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .config-close:hover {
          background: var(--color-stone-100);
          color: var(--color-stone-700);
        }

        .config-content {
          padding: 0 24px;
        }

        .config-section {
          margin-bottom: 20px;
        }

        .config-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: var(--color-slate-900);
          margin-bottom: 8px;
        }

        .current-location {
          font-weight: 400;
          color: var(--color-stone-600);
        }

        .config-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid var(--color-stone-200);
          border-radius: 8px;
          font-size: 14px;
          color: var(--color-slate-900);
          transition: border-color 0.2s ease;
        }

        .config-input:focus {
          outline: none;
          border-color: var(--color-teal-500);
          box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
        }

        .config-input:disabled {
          background: var(--color-stone-50);
          color: var(--color-stone-500);
        }

        .config-hint {
          font-size: 12px;
          color: var(--color-stone-500);
          margin-top: 6px;
        }

        .config-error {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-size: 14px;
        }

        .config-testing {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: var(--color-teal-50);
          border: 1px solid var(--color-teal-200);
          border-radius: 8px;
          color: var(--color-teal-700);
          font-size: 14px;
        }

        .testing-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid var(--color-teal-200);
          border-top: 2px solid var(--color-teal-500);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .config-actions {
          display: flex;
          gap: 12px;
          padding: 24px;
          border-top: 1px solid var(--color-stone-100);
        }

        .config-btn {
          flex: 1;
          padding: 12px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .config-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .config-btn-secondary {
          background: var(--color-stone-100);
          border: 1px solid var(--color-stone-200);
          color: var(--color-stone-700);
        }

        .config-btn-secondary:hover:not(:disabled) {
          background: var(--color-stone-200);
        }

        .config-btn-primary {
          background: var(--color-teal-500);
          border: 1px solid var(--color-teal-500);
          color: white;
        }

        .config-btn-primary:hover:not(:disabled) {
          background: var(--color-teal-600);
          border-color: var(--color-teal-600);
        }

        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}